import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useCookies } from "react-cookie";
import { ErrorBoundary } from "react-error-boundary";
import axios from "axios";
import DisableDevtool from "disable-devtool";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "react-query";

import Router from "./router";
import ErrorPage from "./pages/error";
import { ThemeStore } from "./context/ThemeStore";

import "./styles/common.scss";
import { ThemeProvider } from "styled-components";
import { useSelector } from "react-redux";
import { ConfigProvider, theme } from "antd";
import PreparationDetailsModal from "./components/account/v2/PreparationDetailsModal";
import CacheInvalidator from "./components/global/CacheInvalidator";
import SessionExpiredModal from "./components/global/SessionExpiredModal";
import { useSearchParams } from "react-router-dom";
import { getReactCookieTokenOptions } from "./utils/tokenCookieOptions";

const queryClient = new QueryClient();
const UP = JSON.parse(localStorage.getItem("userProfile"));

const App = () => {
  const [cookies, setCookies] = useCookies();
  const [searchParams] = useSearchParams();
  const { isDark } = useSelector((state) => state.user);

  useEffect(() => {
    const setCookieHeader = async () => {
      if (searchParams.get("t")) {
        setCookies("token", searchParams.get("t"), getReactCookieTokenOptions());
        axios.defaults.headers.common["Authorization"] = `Bearer ${searchParams.get("t")}`;
        window.localStorage.removeItem("userProfile");
      } else {
        axios.defaults.headers.common["Authorization"] = `Bearer ${cookies["token"]}`;
      }
      if (window.location.pathname.includes("/auth") || window.location.pathname.includes("/analysis-demo")) {
        return;
      }
      const res = await axios.get("/api/auth/user");
      localStorage.setItem("userProfile", JSON.stringify(res?.data?.data));
    };
    setCookieHeader();
  }, []);

  // ✅ Apply dark theme class to <body>
  useEffect(() => {
    const body = document.body;
    if (isDark) {
      body.classList.add("dark-theme");
    } else {
      body.classList.remove("dark-theme");
    }
  }, [isDark]);

  return (
    <ErrorBoundary
      onError={(error, componentStack) => {
        console.log({ error, componentStack });
      }}
      fallbackRender={({ resetErrorBoundary }) => <ErrorPage type={503} resetErrorBoundary={resetErrorBoundary} />}
    >
      <ThemeStore>
        <CacheInvalidator />
        <Helmet>
          <meta charSet="UTF-8" />
          <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
          <meta name="robots" content="imageindex, follow, snippet, index" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>{`IIT, JEE Mains | MathonGo`}</title>
          <link rel="manifest" href="/manifest.json" />
          <link href="/icons/favicon-16x16.png" rel="icon" type="image/png" sizes="16x16" />
          <link href="/icons/favicon-32x32.png" rel="icon" type="image/png" sizes="32x32" />
          <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
          <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#2e005b" />
          <meta name="theme-color" content="#317EFB" />
          <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap" rel="stylesheet" />
        </Helmet>

        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={{ isDark }}>
            <ConfigProvider
              theme={{
                algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
                token: isDark ? THEME.dark : THEME.light,
              }}
            >
              <PreparationDetailsModal />
              <SessionExpiredModal />
              <Toaster />
              <Router />
            </ConfigProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </ThemeStore>
    </ErrorBoundary>
  );
};

const THEME = {
  light: {},
  dark: {
    colorBgContainer: "#152e4d",
    colorBgElevated: "#152e4d",
    colorText: "#fff",
  },
};

export default App;
